/**
 * RAG Search Service
 *
 * Connects the frontend search to the RAG-based backend API.
 * This is the MAIN search tool for the website.
 */

import { resolveApiBaseUrl } from '../utils/apiClient';

const API_BASE_URL = resolveApiBaseUrl();

/**
 * Resolve image URL for a relative path.
 *
 * Property images are served from the host ROOT (/uploads/..., /property_images/...),
 * NOT under the API's /api prefix. API_BASE_URL is often "http://host:4000/api", so
 * we must resolve images against the ORIGIN (strip a trailing /api) — otherwise the
 * URL becomes /api/uploads/... and 404s. This was the cause of broken listing photos.
 */
function imageOrigin(): string {
  // Strip a trailing "/api" (and any trailing slash) from the API base.
  const base = (API_BASE_URL || '').replace(/\/+$/, '');
  return base.replace(/\/api$/, '');
}

function resolveImageUrl(imageUrl: string | undefined): string {
  if (!imageUrl) return '';
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  const path = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
  return `${imageOrigin()}${path}`;
}

export interface SearchProperty {
  id: string;
  title: string;
  titleAr?: string;
  description?: string;
  descriptionAr?: string;
  price: number;
  currency: string;
  rentPeriod?: string;
  bedrooms: number;
  bathrooms: number;
  sqft?: number;
  city: string;
  cityAr?: string;
  /** The listing's district (حي), English and Arabic names. */
  area?: string | null;
  areaAr?: string | null;
  state?: string;
  stateAr?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  propertyType: string;
  listingType?: string;
  status?: string;
  isFurnished?: boolean;
  isPetFriendly?: boolean;
  isFeatured?: boolean;
  hasParking?: boolean;
  parkingSpaces?: number;
  yearBuilt?: number;
  floors?: number;
  livingRooms?: number;
  orientation?: string;
  imageUrl?: string;
  // Backend property list returns galleryImages (toPropertyResponse). Older
  // search endpoints used imageUrls. Accept both so a property's full image
  // set survives the transform into a SearchProperty.
  imageUrls?: string[];
  galleryImages?: string[];
  // Media flags so the search card can show what the listing has (video / 360 /
  // floor plan), matching the property detail page.
  videoUrl?: string | null;
  virtualTourUrl?: string | null;
  floorPlanImages?: string[];
  amenities?: string[];
  utilities?: string[];
  agentName?: string;
  agentPhone?: string;
  agentEmail?: string;
  companyId?: string | null;
  companyName?: string | null;
  companyNameAr?: string | null;
  companySlug?: string | null;
  slug?: string;
  relevanceScore?: number;
  isFallback?: boolean;
  viewCount?: number;
  favoriteCount?: number;
  inquiryCount?: number;
  dateAdded?: string;
  /** GET /search sends the publish date as createdAt. */
  createdAt?: string;
  lastUpdated?: string;
  floorLevel?: number;
  apartmentNumber?: string;
  /** AI "why this matches" explanation (paid tiers). */
  matchExplanation?: string;
}

export interface SearchFilters {
  city?: string;
  propertyType?: string;
  minPrice?: number;
  maxPrice?: number;
  bedrooms?: number;
  bathrooms?: number;
  furnished?: boolean;
  petFriendly?: boolean;
  parking?: boolean;
  amenities?: string[];
}

export interface SearchPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

export interface SearchTimings {
  keywordMs: number;
  embedMs: number;
  vectorMs: number;
  mergeMs: number;
  rerankMs: number;
  totalMs: number;
}

export interface DidYouMean {
  original: string;
  corrected: string;
  perToken: Array<{ from: string; to: string }>;
}

export interface SearchMeta {
  searchMethod: 'vector' | 'hybrid' | 'database';
  query: string;
  processingTime: number;
  reranked?: boolean;
  vectorColumn?: string;
  timings?: SearchTimings;
  didYouMean?: DidYouMean | null;
}

export type AiTier = 'none' | 'basic' | 'advanced' | 'premium';

export interface SearchAiInfo {
  tier: AiTier;
  eligible: boolean;
  enabled: boolean;
  /** True when AI/semantic search is globally disabled (the "Coming soon" state). */
  comingSoon?: boolean;
  reranked?: boolean;
  requiresSubscription: boolean;
  premiumAvailable?: boolean;
  quotaRemaining?: number | null;
}

export interface SearchSuggestions {
  properties?: Array<{ title: string; slug: string }>;
  categories?: Array<{ label: string; type: string }>;
}

export interface SearchResponse {
  success: boolean;
  data?: {
    properties: SearchProperty[];
    pagination: SearchPagination;
    filters: {
      applied: SearchFilters;
      description: string;
    };
    meta: SearchMeta;
    ai?: SearchAiInfo;
    suggestions?: SearchSuggestions;
  };
  error?: string;
  suggestions?: SearchSuggestions;
}

export interface SearchSuggestion {
  type: 'location' | 'property_type' | 'popular';
  text: string;
  query: string;
}

export interface SuggestionsResponse {
  success: boolean;
  suggestions: SearchSuggestion[];
}

export interface SearchRequest {
  q?: string;
  city?: string;
  /** Exact City id — deterministic city filter (preferred over fuzzy `city`). */
  cityId?: string;
  areaId?: string;
  /** Radius search (km) around a centroid — pass with `lat` + `lng`. */
  lat?: number;
  lng?: number;
  radius?: number;
  propertyType?: string;
  /** Macro-category — expands server-side to a set of property types. */
  category?: 'residential' | 'commercial';
  /** Featured/exclusive listings only (public — distinct from the gated /special-deals). */
  featured?: boolean;
  listingType?: string; // 'RENT' | 'SALE' | 'SHORT_RENT'
  minPrice?: number;
  maxPrice?: number;
  /** Rent only: the period minPrice / maxPrice are in (the backend converts each listing's stated period). */
  pricePeriod?: 'monthly' | 'yearly';
  bedrooms?: number;
  bathrooms?: number;
  bedroomsMax?: number;
  bathroomsMax?: number;
  minArea?: number;
  maxArea?: number;
  furnished?: boolean;
  petFriendly?: boolean;
  parking?: boolean;
  amenities?: string[];
  swimmingPool?: boolean;
  gym?: boolean;
  garden?: boolean;
  elevator?: boolean;
  security?: boolean;
  ai?: boolean;
  /** Conversational refine: JSON of the previous search's filters (premium). */
  refine?: string;
  token?: string | null;
  page?: number;
  limit?: number;
  sort?: 'relevance' | 'price-asc' | 'price-desc' | 'newest' | 'popular';
  lang?: 'en' | 'ar';
}

/**
 * Search properties using RAG-based backend
 */
export async function searchProperties(request: SearchRequest): Promise<SearchResponse> {
  try {
    // Build query string
    const params = new URLSearchParams();

    if (request.q) params.set('q', request.q);
    if (request.city) params.set('city', request.city);
    if (request.cityId) params.set('cityId', request.cityId);
    if (request.areaId) params.set('areaId', request.areaId);
    if (request.lat !== undefined) params.set('lat', String(request.lat));
    if (request.lng !== undefined) params.set('lng', String(request.lng));
    if (request.radius !== undefined) params.set('radius', String(request.radius));
    if (request.propertyType) params.set('propertyType', request.propertyType);
    if (request.category) params.set('category', request.category);
    if (request.featured !== undefined) params.set('featured', String(request.featured));
    if (request.listingType) params.set('listingType', request.listingType);
    if (request.minPrice !== undefined) params.set('minPrice', String(request.minPrice));
    if (request.maxPrice !== undefined) params.set('maxPrice', String(request.maxPrice));
    if (request.pricePeriod) params.set('pricePeriod', request.pricePeriod);
    if (request.bedrooms !== undefined) params.set('bedrooms', String(request.bedrooms));
    if (request.bathrooms !== undefined) params.set('bathrooms', String(request.bathrooms));
    if (request.bedroomsMax !== undefined) params.set('bedroomsMax', String(request.bedroomsMax));
    if (request.bathroomsMax !== undefined) params.set('bathroomsMax', String(request.bathroomsMax));
    if (request.minArea !== undefined) params.set('minArea', String(request.minArea));
    if (request.maxArea !== undefined) params.set('maxArea', String(request.maxArea));
    if (request.furnished !== undefined) params.set('furnished', String(request.furnished));
    if (request.petFriendly !== undefined) params.set('petFriendly', String(request.petFriendly));
    if (request.parking !== undefined) params.set('parking', String(request.parking));
    if (request.amenities?.length) params.set('amenities', request.amenities.join(','));
    if (request.swimmingPool !== undefined) params.set('swimmingPool', String(request.swimmingPool));
    if (request.gym !== undefined) params.set('gym', String(request.gym));
    if (request.garden !== undefined) params.set('garden', String(request.garden));
    if (request.elevator !== undefined) params.set('elevator', String(request.elevator));
    if (request.security !== undefined) params.set('security', String(request.security));
    if (request.ai !== undefined) params.set('ai', String(request.ai));
    if (request.refine) params.set('refine', request.refine);
    if (request.page) params.set('page', String(request.page));
    if (request.limit) params.set('limit', String(request.limit));
    if (request.sort) params.set('sort', request.sort);
    if (request.lang) params.set('lang', request.lang);

    const response = await fetch(`${API_BASE_URL}/search?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(request.token ? { Authorization: `Bearer ${request.token}` } : {}),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP error: ${response.status}`,
      };
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Search API error:', error);
    return {
      success: false,
      error: 'Failed to connect to search service. Please try again.',
    };
  }
}

/**
 * Fire-and-forget: record that a search result was clicked (Step 0.5 analytics).
 * The backend links it to the user's most recent search (resultsClicked++,
 * timeToFirstClick) and stores a CLICK event. Never throws, never blocks nav.
 */
export function trackSearchResultClick(propertyId: string, position?: number, token?: string | null): void {
  try {
    void fetch(`${API_BASE_URL}/search/click`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ propertyId, position }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* analytics must never affect UX */
  }
}

/**
 * Advanced search with POST body (for complex queries)
 */
export async function advancedSearch(
  query: string,
  filters: SearchFilters = {},
  options: { page?: number; limit?: number; sort?: string } = {}
): Promise<SearchResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        filters,
        page: options.page || 1,
        limit: options.limit || 12,
        sort: options.sort || 'relevance',
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP error: ${response.status}`,
      };
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Advanced search API error:', error);
    return {
      success: false,
      error: 'Failed to connect to search service. Please try again.',
    };
  }
}

/**
 * Get search suggestions
 */
export async function getSearchSuggestions(
  query: string,
  lang: 'en' | 'ar' = 'en',
  limit: number = 5
): Promise<SuggestionsResponse> {
  try {
    const params = new URLSearchParams({
      q: query,
      limit: String(limit),
      lang,
    });

    const response = await fetch(`${API_BASE_URL}/search/suggestions?${params.toString()}`);

    if (!response.ok) {
      return { success: false, suggestions: [] };
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Suggestions API error:', error);
    return { success: false, suggestions: [] };
  }
}

/**
 * Get similar properties
 */
export async function getSimilarProperties(
  propertyId: string,
  limit: number = 4,
  lang: 'en' | 'ar' = 'en'
): Promise<SearchProperty[]> {
  try {
    const params = new URLSearchParams({
      limit: String(limit),
      lang,
    });

    const response = await fetch(`${API_BASE_URL}/search/similar/${propertyId}?${params.toString()}`);

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data.success ? data.data.properties : [];
  } catch (error) {
    console.error('Similar properties API error:', error);
    return [];
  }
}

/**
 * Convert SearchProperty to frontend Property format
 */
export function toFrontendProperty(property: SearchProperty) {
  // Map backend status to frontend status
  const statusMap: Record<string, string> = {
    'ACTIVE': 'available',
    'PENDING': 'pending',
    'SOLD': 'sold',
    'RENTED': 'rented',
    'EXPIRED': 'sold',
    'DRAFT': 'available',
  };

  // Combine every image source the backend might return — galleryImages from
  // /properties (toPropertyResponse) and imageUrls from legacy search
  // endpoints — into a deduplicated, fully-resolved list. Falls back to the
  // single primary imageUrl if both are empty.
  const galleryCandidates = [
    ...(property.galleryImages ?? []),
    ...(property.imageUrls ?? []),
  ];
  const resolvedImages: string[] = [];
  const seen = new Set<string>();
  const pushImage = (raw?: string) => {
    if (!raw) return;
    const resolved = resolveImageUrl(raw);
    if (resolved && !seen.has(resolved)) {
      seen.add(resolved);
      resolvedImages.push(resolved);
    }
  };
  pushImage(property.imageUrl);
  galleryCandidates.forEach(pushImage);

  return {
    id: property.id,
    title: property.title,
    titleAr: property.titleAr,
    description: property.description || '',
    descriptionAr: property.descriptionAr || '',
    address: property.address || '',
    city: property.city,
    cityAr: property.cityAr,
    district: property.area || undefined,
    districtAr: property.areaAr || undefined,
    state: property.state || '',
    stateAr: property.stateAr || '',
    price: property.price,
    bedrooms: property.bedrooms,
    bathrooms: property.bathrooms,
    sqft: property.sqft || 0,
    propertyType: property.propertyType,
    listingType: property.listingType || 'SALE',
    rentPeriod: property.rentPeriod,
    imageUrl: resolveImageUrl(property.imageUrl),
    galleryImages: resolvedImages,
    // Media so the card can badge video / virtual-tour / floor-plan availability.
    videoUrl: property.videoUrl ?? null,
    virtualTourUrl: property.virtualTourUrl ? resolveImageUrl(property.virtualTourUrl) : null,
    hasVirtualTour: !!property.virtualTourUrl,
    floorPlanImages: (property.floorPlanImages ?? [])
      .map((u) => resolveImageUrl(u))
      .filter(Boolean) as string[],
    petFriendly: property.isPetFriendly || false,
    parking: property.hasParking ?? (property.parkingSpaces ? property.parkingSpaces > 0 : false),
    parkingSpaces: property.parkingSpaces || 0,
    furnished: property.isFurnished || false,
    amenities: property.amenities || [],
    utilities: property.utilities || [],
    status: statusMap[property.status || ''] || 'available',
    featured: property.isFeatured || false,
    yearBuilt: property.yearBuilt || new Date().getFullYear(),
    floors: property.floors,
    livingRooms: property.livingRooms,
    orientation: property.orientation,
    agentName: property.agentName || '',
    agentPhone: property.agentPhone || '',
    agentEmail: property.agentEmail || '',
    companyId: property.companyId ?? null,
    companyName: property.companyName ?? null,
    companyNameAr: property.companyNameAr ?? null,
    companySlug: property.companySlug ?? null,
    // GET /search sends createdAt. No date means unknown, never "today":
    // a made-up date would tag every result «جديد».
    dateAdded: property.dateAdded || property.createdAt || '',
    lastUpdated: property.lastUpdated || new Date().toISOString(),
    latitude: property.latitude || 0,
    longitude: property.longitude || 0,
    zipCode: '',
    pricePerSqft: property.sqft && property.sqft > 0 ? Math.round(property.price / property.sqft) : 0,
    viewCount: property.viewCount || 0,
    favoriteCount: property.favoriteCount || 0,
    inquiryCount: property.inquiryCount || 0,
    floorLevel: property.floorLevel,
    apartmentNumber: property.apartmentNumber,
    matchExplanation: property.matchExplanation,
  };
}

export interface AutocompleteSuggestion {
  id: string;
  title: string;
  city: string;
  propertyType: string;
  price: number;
  slug: string;
  imageUrl?: string;
}

export async function fetchAutocomplete(query: string, lang = 'en'): Promise<AutocompleteSuggestion[]> {
  if (!query || query.length < 2) return [];
  const res = await fetch(`${API_BASE_URL}/search/autocomplete?q=${encodeURIComponent(query)}&lang=${lang}`);
  const data = await res.json();
  return data.success ? data.data : [];
}

export default {
  searchProperties,
  advancedSearch,
  getSearchSuggestions,
  getSimilarProperties,
  toFrontendProperty,
  fetchAutocomplete,
};
